import { OnboardingActionTypes } from "models/actionTypes/onboardingActionTypes";
import { ReduxConstants } from "utils/constants";
import { OnboardingSession } from "models/stateTypes/authState";
import { OnboardingPersonSession } from "models/stateTypes/onboardingState";
import {
  removeCompanySession,
  removePersonSession,
  writeCompanySession,
  writePersonSession,
} from "utils/helpers/onboardingSessionStorage";

export const setInviteToken = (inviteToken: string | null): OnboardingActionTypes => {
  return {
    type: ReduxConstants.OnboardingActions.SET_INVITE_TOKEN,
    payload: inviteToken,
  };
};

/**
 * Persists the minted session and puts it in the store.
 *
 * Storage is written here rather than in a reducer because reducers must stay pure, and
 * because this write cannot be deferred: Core hands the session over on one response only,
 * so anything that delays it risks losing the session and forcing the visitor to restart
 * BankID.
 */
export const sessionMinted = (session: OnboardingSession): OnboardingActionTypes => {
  writeCompanySession(session.inviteToken, session);
  return {
    type: ReduxConstants.OnboardingActions.SESSION_MINTED,
    payload: session,
  };
};

/**
 * The counterpart to sessionMinted: the gate refused the session, so it is cleared from both
 * storage and the store and the wizard falls back to identification.
 *
 * Only ever dispatched for AuthType.Onboarding. The generic 401 path must not be reached for
 * an onboarding call -- it reads and clears user_session, which belongs to a logged-in Wint
 * user who may be sitting in another tab.
 *
 * Takes the invite explicitly rather than reading it from the store, so the slot cleared is
 * always the one the ended session actually used.
 */
export const sessionExpired = (inviteToken: string | null): OnboardingActionTypes => {
  if (inviteToken) {
    removeCompanySession(inviteToken);
  }
  return {
    type: ReduxConstants.OnboardingActions.SESSION_EXPIRED,
    payload: Date.now(),
  };
};

export const setCompanyName = (companyName: string | null): OnboardingActionTypes => {
  return {
    type: ReduxConstants.OnboardingActions.SET_COMPANY_NAME,
    payload: companyName,
  };
};

/**
 * Persisted before it reaches the store for the same reason as sessionMinted: the response that
 * delivers the list is the only one that ever does, so a lost write is a repeated BankID.
 */
export const personSessionSaved = (session: OnboardingPersonSession): OnboardingActionTypes => {
  writePersonSession(session);
  return {
    type: ReduxConstants.OnboardingActions.PERSON_SESSION_SAVED,
    payload: session,
  };
};

/** Logout, or the person session was refused: nothing minted from it may outlive it. */
export const personSessionEnded = (): OnboardingActionTypes => {
  removePersonSession();
  return {
    type: ReduxConstants.OnboardingActions.PERSON_SESSION_ENDED,
    payload: Date.now(),
  };
};
